import { useSyncExternalStore, type ChangeEvent } from 'react'
import { GlobalAudio, type AudioVolumes } from '@game/systems/AudioSystem'
import './AudioSettings.css'

const LABELS: Record<keyof AudioVolumes, string> = {
  master: 'Volume geral',
  music: 'Música',
  sfx: 'Efeitos',
  ui: 'Interface',
}

const ORDER: Array<keyof AudioVolumes> = ['master', 'music', 'sfx', 'ui']

const pctFmt = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  maximumFractionDigits: 0,
})

// Painel de configurações de áudio — fonte da verdade é o GlobalAudio.
// Lê via useSyncExternalStore (sem estado React redundante).
export default function AudioSettings() {
  const volumes = useSyncExternalStore(
    (cb) => GlobalAudio.subscribeVolume(cb),
    () => GlobalAudio.getVolumes(),
    () => GlobalAudio.getVolumes(),
  )

  const hapticsEnabled = useSyncExternalStore(
    (cb) => GlobalAudio.subscribeVolume(cb),
    () => GlobalAudio.isHapticsEnabled(),
    () => GlobalAudio.isHapticsEnabled(),
  )

  const videosEnabled = useSyncExternalStore(
    (cb) => GlobalAudio.subscribeVolume(cb),
    () => GlobalAudio.isVideosEnabled(),
    () => GlobalAudio.isVideosEnabled(),
  )

  const handleChange =
    (cat: keyof AudioVolumes) => (e: ChangeEvent<HTMLInputElement>) => {
      GlobalAudio.setVolume(cat, Number(e.target.value) / 100)
    }

  const handleToggleHaptics = () => {
    const nextVal = !hapticsEnabled
    GlobalAudio.setHapticsEnabled(nextVal)
    // Tenta vibrar brevemente como feedback físico para o jogador
    if (nextVal) {
      GlobalAudio.vibrate(20)
    }
  }

  const handleToggleVideos = () => {
    GlobalAudio.setVideosEnabled(!videosEnabled)
  }

  const handleMute = () => GlobalAudio.setVolume('master', 0)
  const handleReset = () => {
    GlobalAudio.resetVolumes()
    GlobalAudio.setHapticsEnabled(true)
  }

  return (
    <div className="audio-settings">
      <div className="audio-settings__grid">
        {ORDER.map((cat) => (
          <Slider
            key={cat}
            cat={cat}
            label={LABELS[cat]}
            value={volumes[cat]}
            onChange={handleChange(cat)}
          />
        ))}
      </div>

      <div className="haptics-settings">
        <label className="haptics-toggle-label">
          <input
            type="checkbox"
            checked={hapticsEnabled}
            onChange={handleToggleHaptics}
            className="haptics-checkbox"
          />
          <span className="haptics-toggle-switch"></span>
          <span className="haptics-toggle-text">Vibração no Controle</span>
        </label>

        <label className="haptics-toggle-label">
          <input
            type="checkbox"
            checked={videosEnabled}
            onChange={handleToggleVideos}
            className="haptics-checkbox"
          />
          <span className="haptics-toggle-switch"></span>
          <span className="haptics-toggle-text">Vídeos de Introdução</span>
        </label>
      </div>

      <div className="audio-settings__actions">
        <button
          type="button"
          className="audio-settings__btn"
          onClick={handleMute}
          aria-label="Silenciar todos os volumes"
        >
          Silenciar
        </button>
        <button
          type="button"
          className="audio-settings__btn"
          onClick={handleReset}
          aria-label="Restaurar volumes padrão"
        >
          Restaurar padrão
        </button>
      </div>
    </div>
  )
}

interface SliderProps {
  cat: keyof AudioVolumes
  label: string
  value: number
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
}

function Slider({ cat, label, value, onChange }: SliderProps) {
  const pct = Math.round(value * 100)
  const inputId = `audio-${cat}`
  return (
    <div className="audio-slider">
      <div className="audio-slider__row">
        <label htmlFor={inputId} className="audio-slider__label">
          {label}
        </label>
        <span className="audio-slider__value" aria-hidden="true">
          {pctFmt.format(pct / 100)}
        </span>
      </div>
      <input
        id={inputId}
        type="range"
        min={0}
        max={100}
        step={1}
        value={pct}
        onChange={onChange}
        className="audio-slider__input"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-valuetext={`${pct} por cento`}
      />
    </div>
  )
}
